import { supabase } from './supabase';

export interface EmailStatus {
  success: boolean;
  message: string;
  timestamp: Date;
  recipient: string;
  subject?: string;
  error?: any;
}

const emailStatusHistory: EmailStatus[] = [];

export const addEmailStatus = (status: EmailStatus) => {
  emailStatusHistory.unshift(status);

  if (emailStatusHistory.length > 100) {
    emailStatusHistory.pop();
  }

  const event = new CustomEvent('emailStatusUpdate', { detail: status });
  window.dispatchEvent(event);

  return status;
};

export const getEmailStatusHistory = () => {
  return [...emailStatusHistory];
};

const callEmailFunction = async (payload: {
  emailType: string;
  to: string;
  placeholders?: Record<string, string>;
}): Promise<{ success: boolean; message: string; recipient?: string; subject?: string }> => {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token ?? import.meta.env.VITE_SUPABASE_ANON_KEY;

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Email service error (${response.status}): ${text.slice(0, 200)}`);
  }

  return response.json();
};

export const sendConfirmationEmail = async (
  email: string,
  firstName: string,
  registrationNumber: string,
  shirtSize?: string,
  lastName?: string
) => {
  try {
    const result = await callEmailFunction({
      emailType: 'registration_confirmation',
      to: email,
      placeholders: {
        firstName,
        lastName: lastName || '',
        registrationNumber,
        shirtSize: shirtSize || 'Not specified',
      },
    });

    return addEmailStatus({
      success: result.success,
      message: result.message,
      timestamp: new Date(),
      recipient: email,
      subject: result.subject,
    });
  } catch (error: any) {
    return addEmailStatus({
      success: false,
      message: `Error: ${error.message || 'Failed to send confirmation email'}`,
      timestamp: new Date(),
      recipient: email,
      error,
    });
  }
};

export const sendPaymentConfirmationEmail = async (
  email: string,
  firstName: string,
  amount: string,
  reference: string,
  lastName?: string
) => {
  try {
    const result = await callEmailFunction({
      emailType: 'payment_confirmation',
      to: email,
      placeholders: {
        firstName,
        lastName: lastName || '',
        amount,
        reference,
      },
    });

    return addEmailStatus({
      success: result.success,
      message: result.message,
      timestamp: new Date(),
      recipient: email,
      subject: result.subject,
    });
  } catch (error: any) {
    return addEmailStatus({
      success: false,
      message: `Error: ${error.message || 'Failed to send payment confirmation email'}`,
      timestamp: new Date(),
      recipient: email,
      error,
    });
  }
};

export const sendWelcomeEmail = async (
  email: string,
  firstName: string,
  registrationNumber: string,
  shirtSize?: string,
  lastName?: string,
  tournamentData?: {
    tournamentName?: string;
    tournamentDate?: string;
    venue?: string;
  }
) => {
  try {
    const result = await callEmailFunction({
      emailType: 'welcome',
      to: email,
      placeholders: {
        firstName,
        lastName: lastName || '',
        registrationNumber,
        shirtSize: shirtSize || 'Not specified',
        ...(tournamentData?.tournamentName && { tournamentName: tournamentData.tournamentName }),
        ...(tournamentData?.tournamentDate && { tournamentDate: tournamentData.tournamentDate }),
        ...(tournamentData?.venue && { venue: tournamentData.venue }),
      },
    });

    return addEmailStatus({
      success: result.success,
      message: result.message,
      timestamp: new Date(),
      recipient: email,
      subject: result.subject,
    });
  } catch (error: any) {
    return addEmailStatus({
      success: false,
      message: `Error: ${error.message || 'Failed to send welcome email'}`,
      timestamp: new Date(),
      recipient: email,
      error,
    });
  }
};

export const sendAdminInviteEmail = async (
  email: string,
  inviteLink: string
) => {
  try {
    const result = await callEmailFunction({
      emailType: 'admin_invite',
      to: email,
      placeholders: { inviteLink },
    });

    return addEmailStatus({
      success: result.success,
      message: result.message,
      timestamp: new Date(),
      recipient: email,
      subject: result.subject,
    });
  } catch (error: any) {
    return addEmailStatus({
      success: false,
      message: `Error: ${error.message || 'Failed to send admin invite email'}`,
      timestamp: new Date(),
      recipient: email,
      error,
    });
  }
};

export const sendInterestConfirmationEmail = async (
  email: string,
  firstName: string,
  lastName: string,
  reference: string,
  arrivalDate: string,
  departureDate: string,
  roomType: string,
  numAdults: number,
  numChildren: number,
) => {
  const roomLabels: Record<string, string> = {
    none: 'No room required',
    standard: 'Standard Room',
    deluxe: 'Deluxe Room',
    suite: 'Suite',
    villa: 'Villa',
  };
  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

  try {
    const result = await callEmailFunction({
      emailType: 'interest_confirmation',
      to: email,
      placeholders: {
        firstName,
        lastName,
        reference,
        arrivalDate: formatDate(arrivalDate),
        departureDate: formatDate(departureDate),
        numAdults: String(numAdults),
        numChildren: String(numChildren),
        roomType: roomLabels[roomType] || roomType,
      },
    });

    return addEmailStatus({
      success: result.success,
      message: result.message,
      timestamp: new Date(),
      recipient: email,
      subject: result.subject,
    });
  } catch (error: any) {
    return addEmailStatus({
      success: false,
      message: `Error: ${error.message || 'Failed to send interest confirmation email'}`,
      timestamp: new Date(),
      recipient: email,
      error,
    });
  }
};

export const sendAnnouncementEmail = async (
  email: string,
  firstName: string,
  headline: string,
  message: string
) => {
  try {
    const result = await callEmailFunction({
      emailType: 'announcement',
      to: email,
      placeholders: {
        firstName,
        headline,
        message,
      },
    });

    return addEmailStatus({
      success: result.success,
      message: result.message,
      timestamp: new Date(),
      recipient: email,
      subject: result.subject,
    });
  } catch (error: any) {
    return addEmailStatus({
      success: false,
      message: `Error: ${error.message || 'Failed to send announcement email'}`,
      timestamp: new Date(),
      recipient: email,
      error,
    });
  }
};

export const sendSampleEmail = async (
  emailType: string,
  to: string,
  placeholders: Record<string, string>
): Promise<EmailStatus> => {
  try {
    const result = await callEmailFunction({
      emailType,
      to,
      placeholders,
    });

    return addEmailStatus({
      success: result.success,
      message: result.message,
      timestamp: new Date(),
      recipient: to,
      subject: result.subject,
    });
  } catch (error: any) {
    return addEmailStatus({
      success: false,
      message: `Error: ${error.message || 'Failed to send sample email'}`,
      timestamp: new Date(),
      recipient: to,
      error,
    });
  }
};

export const sendStatusChangeEmail = async (
  email: string,
  firstName: string,
  status: string,
  lastName?: string
) => {
  try {
    const result = await callEmailFunction({
      emailType: 'status_change',
      to: email,
      placeholders: {
        firstName,
        lastName: lastName || '',
        status,
      },
    });

    return addEmailStatus({
      success: result.success,
      message: result.message,
      timestamp: new Date(),
      recipient: email,
      subject: result.subject,
    });
  } catch (error: any) {
    return addEmailStatus({
      success: false,
      message: `Error: ${error.message || 'Failed to send status change email'}`,
      timestamp: new Date(),
      recipient: email,
      error,
    });
  }
};
